/**
 * Formato del RUT chileno.
 *
 * A pure, stateless function on purpose: `RutText`'s contract
 * (`docs/FRONTEND_COMPONENT_INVENTORY.md` §5.4) says this formatter is shared
 * with `RutInput` (§5.2, another wave) — a single place that knows how to put
 * the dots and the hyphen in, so the two components do not diverge.
 */

/** The two formats `RutText`'s contract asks for. */
export type RutFormat = 'display' | 'plain';

/**
 * Strips dots, hyphens and spaces; leaves only digits and the DV, uppercase
 * (the DV can be `K`). It tolerates a RUT arriving already formatted (with
 * dots) because `RutInput` feeds it whatever the user is typing, not only the
 * API's response.
 */
function stripRut(rut: string): string {
  return rut.replace(/[^0-9kK]/g, '').toUpperCase();
}

/**
 * Formats a complete RUT (body + dígito verificador).
 *
 * The API serves it without dots and with a hyphen — `76192083-9`, verified in
 * `src/test/mocks/handlers.ts` (`receiverTaxNumber`) and in the generated
 * `DteListItemDto` type — but the function normalises any input first, so a RUT
 * that already comes with dots (or another separator) also formats
 * bien.
 *
 * `display` groups the body in threes from the right (`12.345.678-5`);
 * `plain` is the body with no dots plus the hyphen (`12345678-5`, for pasting
 * into the SII portal).
 *
 * A RUT with no DV (fewer than 2 characters after cleaning) is returned clean
 * and without a hyphen: there is no separator to place and no digits to group.
 */
export function formatRut(rut: string, format: RutFormat = 'display'): string {
  const clean = stripRut(rut);
  if (clean.length < 2) return clean;

  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);

  if (format === 'plain') return `${body}-${dv}`;

  const withDots = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${withDots}-${dv}`;
}

/**
 * Validates the dígito verificador with the modulo-11 algorithm (the same one
 * LibreDTE and the rest of the Chilean facturadores use). Added here and not in
 * `RutInput` (wave 4b, contract §5.2): the DV is a property of the RUT, not of
 * the form field that edits it, and `RutText` / any other future consumer needs
 * it just the same without reimplementing it.
 *
 * It tolerates the same "dirty" input as `formatRut` — with dots, without dots,
 * a lowercase DV — because it goes through `stripRut` first.
 *
 * A RUT with no DV (fewer than 2 characters after cleaning, the same cut-off
 * `formatRut` uses) is invalid: there is nothing to verify yet. It is exactly
 * the "half-typed" state, and it is the reason `RutInput` validates on blur and
 * not on every keystroke — with this function an incomplete RUT always returns
 * `false`, so announcing it key by key would turn the field into a permanent red
 * light while it is being written.
 */
export function isValidRut(rut: string): boolean {
  const clean = stripRut(rut);
  if (clean.length < 2) return false;

  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  if (!/^\d+$/.test(body)) return false;

  let sum = 0;
  let multiplier = 2;
  for (let i = body.length - 1; i >= 0; i -= 1) {
    sum += Number(body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = 11 - (sum % 11);
  const expectedDv = remainder === 11 ? '0' : remainder === 10 ? 'K' : String(remainder);

  return expectedDv === dv;
}
